import {
  MixpanelEvent,
  MixpanelEventSource,
  NotificationVariants,
  useGlobalToasts,
  useMixpanel,
  useUserContext,
  type Security,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getRestFailureUserMessage } from '../../../utils/rtkQuery/restFailureError';
import { useMarketSelectorPanelSaveControls } from '../../MarketSelector/MarketSelectorPanelSaveControls';
import { MultilegQuickMarketSelectorPanel } from '../../MultiLegSummary/MultilegQuickMarketSelectorPanel';
import {
  areMarketAccountSelectionsEqual,
  getMultilegUnifiedLiquidityMarketAccounts,
  shouldUpdateMultilegLegMarketAccounts,
  toMultilegSelectedMarketAccounts,
  updateMultilegLegMarkets,
  useMultilegLegMarketAccountsEditing,
  type LegMarketAccounts,
} from '../multilegLegMarketAccounts';
import type { MarketDataCardQuickMarketSelectorPanelProps } from './MarketDataCardQuickMarketSelectorPanel';

interface MarketDataCardMultilegQuickMarketSelectorPanelProps extends MarketDataCardQuickMarketSelectorPanelProps {
  security: Security;
}

/**
 * Multileg quick market selector intentionally does not use the standard Market Data Card market-selector model.
 *
 * Standard cards store selections as `selectedMarkets`, `selectedMarketAccounts`, optional aggregation
 * presets, and optionally structured `unifiedLiquidityMarketSymbols`. Multileg cards instead keep the
 * synthetic instrument selected via `multileg/multileg`; regular leg markets are persisted on the
 * multileg instrument itself through the multileg API, and the multileg UL leg uses extra
 * `selectedMarketAccounts` values rather than `unifiedLiquidityMarketSymbols`.
 *
 * Watch for two persistence paths here:
 * - leg market edits must be saved with every leg included in `UpdateReqLegs`, including unchanged legs;
 * - card-level selections still need to be persisted so the market card keeps `multileg/multileg` and any
 *   selected multileg UL market accounts after the quick selector closes.
 */
export function MarketDataCardMultilegQuickMarketSelectorPanel({
  security,
  ulEnabled,
  selectedMarketAccounts,
  selectedAggregation,
  onPersistMarketSelection,
  isOpen,
  onClose,
  panelAnchorRef,
  isNarrow,
}: MarketDataCardMultilegQuickMarketSelectorPanelProps) {
  const { legMarketAccounts, onLegMarketAccountsChange, resetLegMarketAccounts } = useMultilegLegMarketAccountsEditing({
    security,
  });
  // The market data card stores the multileg ul leg's selected markets in the card's base "selectedMarketAccounts", eg ["multileg/multileg", "market1/primary"]
  const [multilegMarketAccounts, setMultilegMarketAccounts] = useState(selectedMarketAccounts);
  const [saveLoading, setSaveLoading] = useState(false);
  const { add: addToast } = useGlobalToasts();
  const mixpanel = useMixpanel(MixpanelEventSource.MarketCard);
  const { orgApiEndpoint } = useUserContext();

  // Keep unsaved leg edits scoped to the currently rendered card config.
  // If the parent saves or swaps symbol/config while the panel instance stays mounted, reset local draft state.
  useEffect(() => {
    setMultilegMarketAccounts(selectedMarketAccounts);
  }, [security.Symbol, selectedMarketAccounts]);

  const handleMultilegULMarketsChange = useCallback((marketAccounts: string[]) => {
    setMultilegMarketAccounts(toMultilegSelectedMarketAccounts(marketAccounts));
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setSaveLoading(true);
      const updatedMultilegLegMarkets = await updateMultilegLegMarkets({
        security,
        legMarketAccounts,
        orgApiEndpoint,
      });
      setSaveLoading(false);

      if (updatedMultilegLegMarkets) {
        mixpanel.track(MixpanelEvent.SaveMultilegLegMarkets);
        addToast({
          text: `Symbol ${security.DisplaySymbol ?? security.Symbol} updated.`,
          variant: NotificationVariants.Positive,
        });
      }

      onPersistMarketSelection({
        selectedAggregation,
        selectedMarkets: [],
        selectedMarketAccounts: multilegMarketAccounts,
      });
      onClose();
    } catch (error) {
      setSaveLoading(false);

      addToast({
        text: getRestFailureUserMessage(error) ?? 'Unable to update multileg leg markets.',
        variant: NotificationVariants.Negative,
      });
    }
  }, [
    addToast,
    legMarketAccounts,
    mixpanel,
    multilegMarketAccounts,
    onClose,
    onPersistMarketSelection,
    orgApiEndpoint,
    security,
    selectedAggregation,
  ]);

  const hasUnsavedChanges = useMemo(
    () =>
      hasMultilegMarketAccountSelectionChanges(
        security,
        legMarketAccounts,
        selectedMarketAccounts,
        multilegMarketAccounts
      ),
    [legMarketAccounts, multilegMarketAccounts, security, selectedMarketAccounts]
  );

  const handleCloseWithoutSaving = useCallback(() => {
    setMultilegMarketAccounts(selectedMarketAccounts);
    resetLegMarketAccounts();
    onClose();
  }, [onClose, resetLegMarketAccounts, selectedMarketAccounts]);

  const { panelBottomSuffix, requestClose } = useMarketSelectorPanelSaveControls({
    saveLoading,
    onSave: handleSave,
    onClose,
    onCloseWithoutSaving: handleCloseWithoutSaving,
    hasUnsavedChanges,
  });

  const unifiedLiquidityMarkets = useMemo(
    () => getMultilegUnifiedLiquidityMarketAccounts(multilegMarketAccounts),
    [multilegMarketAccounts]
  );

  return (
    <MultilegQuickMarketSelectorPanel
      isOpen={isOpen}
      onClose={requestClose}
      security={security}
      legMarketAccounts={legMarketAccounts}
      onLegMarketAccountsChange={onLegMarketAccountsChange}
      unifiedLiquidityMarketAccounts={unifiedLiquidityMarkets}
      onUnifiedLiquidityMarketAccountsChange={handleMultilegULMarketsChange}
      includeUnifiedLiquidity={ulEnabled}
      panelAnchorRef={panelAnchorRef}
      panelBottomSuffix={panelBottomSuffix}
      isMarketSelectorNarrow={isNarrow}
    />
  );
}

/**
 * This quick selector edits two backing stores at once: regular leg markets live on the
 * multileg instrument, while the UL leg markets live in the market data card's selected accounts. Hence why
 * we do two separate assertions here. One on the "legs" (leg0, leg1) and one on the "selectedMarketAccounts", which includes the ul leg market accounts
 */
function hasMultilegMarketAccountSelectionChanges(
  security: Security | undefined,
  legMarketAccounts: LegMarketAccounts,
  selectedMarketAccounts: readonly string[],
  panelSelectedMarketAccounts: readonly string[]
): boolean {
  return (
    // First check checks the leg0 leg1 market selections vs the security,
    shouldUpdateMultilegLegMarketAccounts(security, legMarketAccounts) ||
    // second check checks the ul leg's market selections
    !areMarketAccountSelectionsEqual(selectedMarketAccounts, panelSelectedMarketAccounts)
  );
}
